import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../lib/api/apiClient';

export type DateRangeOption = 'all' | 'today' | 'yesterday' | 'last3days' | 'thisweek' | 'thismonth';

export interface TransactionType {
  displayName: string;
  type: string;
}

export interface TransactionFilters {
  teamIds?: number[];
  type?: string | string[]; // Can be a single type or array of types
  playerIds?: number[];
  hoursBack?: number; // Number of hours backwards from now
  myTeam?: number; // Current user's team ID for filtering by can_see
}

export interface UseTransactionsOptions {
  leagueId: number | undefined;
  filters?: TransactionFilters;
  page?: number;
  pageSize?: number;
  debounceMs?: number; // Debounce delay for filter changes (not currently used, but kept for consistency)
}

export function useTransactions({ leagueId, filters = {}, page = 0, pageSize = 50, debounceMs = 300 }: UseTransactionsOptions) {
  // Clean filters - remove empty values and format arrays
  const cleanFilters = useMemo(() => {
    const cleaned: Record<string, unknown> = {
      ...filters,
      page,
      pageSize,
    };
    
    // Remove empty filters
    Object.keys(cleaned).forEach(key => {
      if (cleaned[key] === undefined || cleaned[key] === '' || cleaned[key] === null) {
        delete cleaned[key];
      }
    });
    
    // Keep arrays as arrays for POST request (no need to convert to comma-separated strings)
    // Ensure teamIds and playerIds are numbers
    if (cleaned.teamIds && Array.isArray(cleaned.teamIds)) {
      cleaned.teamIds = cleaned.teamIds.map(id => Number(id)).filter(id => id);
      if (cleaned.teamIds.length === 0) delete cleaned.teamIds;
    }
    if (cleaned.playerIds && Array.isArray(cleaned.playerIds)) {
      cleaned.playerIds = cleaned.playerIds.map(id => Number(id)).filter(id => id);
      if (cleaned.playerIds.length === 0) delete cleaned.playerIds;
    }
    if (cleaned.type && Array.isArray(cleaned.type) && cleaned.type.length > 0) {
      // Keep type as array for POST
    }
    
    // Remove hoursBack if it's 0 or undefined (no date filtering)
    if (cleaned.hoursBack === 0 || cleaned.hoursBack === undefined) {
      delete cleaned.hoursBack;
    }
    
    return cleaned;
  }, [filters, page, pageSize]);

  const queryKey = ['transactions', leagueId, cleanFilters];

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await apiClient.post(`/transactions/${leagueId}`, cleanFilters);
      return res.data;
    },
    enabled: !!leagueId,
  });

  return {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook to fetch transaction types
 * Called once when the transaction page loads
 * Returns an array of transaction types with displayName and type
 */
export function useTransactionTypes() {
  const { data, isLoading, error } = useQuery<TransactionType[]>({
    queryKey: ['transactionTypes'],
    queryFn: async () => {
      const res = await apiClient.get('/transactions/typeList');
      // Handle different response formats
      if (Array.isArray(res.data)) {
        return res.data;
      }
      // If response is wrapped in an object, try to extract the array
      if (res.data && Array.isArray(res.data.types)) {
        return res.data.types;
      }
      if (res.data && Array.isArray(res.data.transactionTypes)) {
        return res.data.transactionTypes;
      }
      // Default to empty array if structure is unexpected
      return [];
    },
    staleTime: Infinity, // Cache forever since transaction types don't change often
    gcTime: Infinity, // Keep in cache forever
  });

  return {
    transactionTypes: Array.isArray(data) ? data : [],
    isLoading,
    error,
  };
}

